import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useLangStore } from "@/store/lang.js";
import { useActivePage } from "@/hooks/useActivePage.js";
import { PublicNav } from "@/components/PublicNav.js";
import { Footer } from "@/components/Footer.js";
import { Carousel } from "@/components/Carousel.js";
import { CardBlock } from "@/components/CardBlock.js";
import { Skeleton } from "@templestay/ui";
import { ComingSoonModal } from "@templestay/ui";
import { uiStrings } from "@templestay/i18n";
import { resolveText } from "@/lib/i18nResolve.js";
import { usePageTitle } from "@/hooks/usePageTitle.js";
import type { MainPageBlock, MainPage as MainPageType } from "@templestay/db";
import type { Language } from "@templestay/i18n";
import { api } from "@/lib/api.js";

export function MainPage() {
  const { lang } = useLangStore();
  const [searchParams] = useSearchParams();
  const previewId = searchParams.get("preview");

  const activeResult = useActivePage(lang);
  const previewResult = useQuery({
    queryKey: ["preview-page", previewId, lang],
    queryFn: () =>
      api.get<{ page: MainPageType & { default_language?: Language }; blocks: MainPageBlock[]; language: string; redirect?: string }>(
        `/api/main-pages/preview/${previewId}?lang=${lang}`
      ),
    enabled: !!previewId,
    staleTime: 0,
    retry: 1,
  });

  const { data, isLoading, isError } = previewId ? previewResult : activeResult;
  const s = uiStrings[lang];
  usePageTitle(s.site.title);

  useEffect(() => {
    if (data?.redirect) {
      window.location.href = data.redirect;
    }
  }, [data?.redirect]);

  if (isLoading) return <MainPageSkeleton />;
  if (isError || !data?.page) return <MainPageEmpty lang={lang} />;

  const defaultLang = (data.page as MainPageType & { default_language?: Language }).default_language || "ko";

  return (
    <div className="flex flex-col min-h-screen pt-3">
      <PublicNav lang={lang} />
      <main className="flex-1 px-3 pt-3 pb-6 space-y-3">
        {data.blocks.map((block, i) => (
          <motion.div
            key={block.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
          >
            <Block block={block} lang={lang} defaultLang={defaultLang} mainPageId={data.page.id} />
          </motion.div>
        ))}
      </main>
      <Footer />
    </div>
  );
}

function Block({
  block,
  lang,
  defaultLang,
  mainPageId,
}: {
  block: MainPageBlock;
  lang: Language;
  defaultLang: Language;
  mainPageId: string;
}) {
  const i18nParsed = (() => {
    try { return JSON.parse(block.i18n) as Record<Language, Record<string, string>>; } catch { return { ko: {}, en: {}, zh: {}, ja: {} } as Record<Language, Record<string, string>>; }
  })();
  const config = JSON.parse(block.config) as Record<string, unknown>;
  const text = resolveText(i18nParsed, lang, defaultLang);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [ctaModalOpen, setCtaModalOpen] = useState(false);

  const s = uiStrings[lang];

  async function trackClick() {
    await api.post("/api/main-pages/track-click", { main_page_id: mainPageId, block_id: block.id, lang }).catch(() => {});
  }

  switch (block.block_type) {
    case "card":
      return <CardBlock block={block} lang={lang} defaultLang={defaultLang} mainPageId={mainPageId} />;

    case "image":
      return config.url ? (
        <div className="overflow-hidden rounded-2xl glass-card-sm">
          <img
            src={config.url as string}
            alt={(text.alt as string | undefined) ?? ""}
            className="w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : null;

    case "carousel": {
      let images: string[] = [];
      if (Array.isArray(config.images)) {
        images = config.images as string[];
      } else if (typeof config.images === "string") {
        try { images = JSON.parse(config.images) as string[]; } catch { images = []; }
      }
      return images.length > 0 ? (
        <div className="overflow-hidden rounded-2xl glass-card-sm">
          <Carousel images={images} />
        </div>
      ) : null;
    }

    case "map":
      return config.url ? (
        <>
          <div className="overflow-hidden rounded-2xl glass-card-sm">
            <button
              onClick={() => setMapModalOpen(true)}
              className="relative block w-full overflow-hidden"
              type="button"
            >
              <img
                src={config.url as string}
                alt={text.map_title ?? ""}
                className="w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <span className="flex items-center gap-1.5 rounded-full glass-nav px-4 py-2 text-sm font-semibold text-gray-800">
                  <svg className="h-4 w-4 shrink-0 text-gray-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                  </svg>
                  {text.map_title || (lang === "ko" ? "사찰 지도 탐색" : lang === "en" ? "Explore Temple Map" : lang === "zh" ? "探索寺庙地图" : "境内マップを探索")}
                </span>
              </div>
            </button>
          </div>
          <ComingSoonModal
            open={mapModalOpen}
            onClose={() => setMapModalOpen(false)}
            lang={lang}
            description={text.modal_desc || s.common.comingSoonDesc}
          />
        </>
      ) : null;

    case "text":
      return (
        <div className="rounded-2xl glass-card-sm px-5 py-4">
          {text.title && (
            <h2 className="mb-1 text-lg font-bold text-gray-900">{text.title}</h2>
          )}
          {text.body && (
            <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">
              {text.body}
            </p>
          )}
        </div>
      );

    case "cta":
      if (config.coming_soon === true || config.coming_soon === "true") {
        return (
          <div className="overflow-hidden rounded-2xl glass-card-sm">
            <button
              type="button"
              onClick={() => setCtaModalOpen(true)}
              className="flex w-full items-center justify-between rounded-2xl bg-white/50 px-5 py-3.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-white/70"
            >
              <span>{text.label || s.common.link}</span>
              <span className="text-xs text-gray-400">{s.common.preparing}</span>
            </button>
            <ComingSoonModal open={ctaModalOpen} onClose={() => setCtaModalOpen(false)} lang={lang} />
          </div>
        );
      }
      return (
        <div className="overflow-hidden rounded-2xl glass-card-sm">
          {config.url ? (
            <a
              href={config.url as string}
              target={String(config.url).startsWith("http") ? "_blank" : undefined}
              rel={String(config.url).startsWith("http") ? "noopener noreferrer" : undefined}
              onClick={trackClick}
              className="flex w-full items-center justify-between rounded-2xl bg-brand-500 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 active:bg-brand-700"
            >
              <span>{text.label || s.common.link}</span>
              <span>→</span>
            </a>
          ) : null}
        </div>
      );

    case "spacing":
      return <div style={{ height: (config.height as number | undefined) ?? 24 }} />;

    case "video":
      return null;

    default:
      return null;
  }
}

function MainPageSkeleton() {
  return (
    <div className="flex flex-col min-h-screen pt-3">
      <div className="sticky top-3 mx-3 h-[46px] shrink-0 rounded-[20px] glass-nav" />
      <div className="px-3 pt-3 space-y-3">
        {/* carousel/image 블록 스켈레톤 */}
        <div className="rounded-2xl glass-card-sm overflow-hidden">
          <Skeleton className="h-52 w-full" />
        </div>
        {/* 텍스트 블록 스켈레톤 */}
        <div className="rounded-2xl glass-card-sm px-5 py-4 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
        {/* CTA 블록 스켈레톤 */}
        <div className="overflow-hidden rounded-2xl glass-card-sm">
          <Skeleton className="h-12 w-full rounded-2xl" />
        </div>
        {/* 카드 블록 스켈레톤 */}
        <div className="rounded-2xl overflow-hidden bg-white/75 border border-white/60 shadow-sm">
          <Skeleton className="h-40 w-full" />
          <div className="px-5 py-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="px-5 pb-4">
            <Skeleton className="h-11 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MainPageEmpty({ lang }: { lang: Language }) {
  const s = uiStrings[lang];
  return (
    <div className="flex min-h-screen flex-col pt-3">
      <div className="sticky top-3 mx-3 h-[46px] shrink-0 rounded-[20px] glass-nav" />
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="glass-card rounded-[28px] px-8 py-8 text-center">
          <p className="text-gray-500">{s.page.errorLoad}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-sm text-brand-500 underline"
          >
            {s.common.retry}
          </button>
        </div>
      </div>
    </div>
  );
}
